import { useEffect, useState } from "react";

import { CloseIcon, HamburgerIcon, MoonIcon, SunIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useColorMode,
  VStack
} from "@chakra-ui/react";
import { SignedIn, SignedOut, useClerk, useUser } from "@clerk/clerk-react";
import { motion } from "framer-motion";
import { CgProfile } from "react-icons/cg";
import { FiSettings } from "react-icons/fi";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { MenuLink, TopMenuLinks } from "./navLinks";

import { useAppState } from "~/hooks/useAppState";
import { useDebugStore } from "~/store/debugStore";

interface NavLinkProps {
  item: MenuLink;
}

const NavLink = ({ item }: NavLinkProps) => {
  const { colorMode } = useColorMode();
  return (
    <Link to={item.path} target={item.target}>
      <Box
        px={2}
        py={1}
        rounded={"md"}
        _hover={{
          textDecoration: "none",
          bg: colorMode === "dark" ? "gray.700" : "gray.200"
        }}
        color={colorMode === "dark" ? "gray.100" : "gray.800"}
      >
        {item.label}
      </Box>
    </Link>
  );
};

interface NavBarProps {
  menuIsVisible: boolean;
  openMenu: () => void;
}

export default function NavBar({ menuIsVisible, openMenu }: NavBarProps) {
  const clerk = useClerk();
  const user = useUser();
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();
  const { userInfo } = useAppState("userInfo");
  const [queryParams, setSearchParams] = useSearchParams();

  // Force a re-render when auth state changes
  const [, setAuthState] = useState<number>(0);

  // Allow toggling of debug mode by d key
  // or include `debug=true` in URL
  useEffect(() => {
    const debugParam = queryParams.get("debug");
    if (debugParam) {
      localStorage.setItem("debugMode", "true");
      // window.location.reload();
    }
  }, []);

  const tallyRender = useDebugStore((state) => state.tallyRender);

  // Track NavBar component renders
  useEffect(() => {
    tallyRender("NavBar");
  }, [tallyRender]);

  useEffect(() => {
    // This effect will run whenever userInfo changes
    // The purpose is to force a re-render of the NavBar component
    // when the user logs in and userInfo is updated
    if (user.isSignedIn && userInfo) {
      console.log("User info updated in NavBar:", userInfo);
      setAuthState((prev) => prev + 1);
    }
  }, [user.isSignedIn, userInfo]);

  const menuVariants = {
    open: { rotate: 90 },
    closed: { rotate: 0 }
  };

  async function handleSignOut() {
    localStorage.removeItem("conversationFormValues");
    localStorage.removeItem("henophess_user_info");
    await clerk.signOut();
  }

  return (
    <Box
      px={4}
      w="100%"
      bg={colorMode === "dark" ? "gray.800" : "brand.background_light"}
      color={colorMode === "dark" ? "gray.100" : "gray.800"}
      borderBottom="1px solid"
      borderBottomColor={colorMode === "dark" ? "gray.700" : "gray.200"}
    >
      <Flex h={16} alignItems={"center"} justifyContent={"space-between"}>
        <Box w={"32px"} h={2} hideFrom={"md"}></Box>
        <Box position="absolute" display={{ md: "none" }} zIndex={1000} left={2}>
          <motion.div
            animate={menuIsVisible ? "open" : "closed"}
            variants={menuVariants}
            transition={{ duration: 0.25 }}
          >
            <IconButton
              aria-label={"Open menu"}
              icon={
                menuIsVisible ? (
                  <CloseIcon color="white" bg="brand.primary" fontSize="md" />
                ) : (
                  <HamburgerIcon bg={"transparent"} color={colorMode === "dark" ? "gray.100" : "gray.800"} />
                )
              }
              onClick={() => openMenu()}
              fontSize={"22px"}
              variant={""}
            />
          </motion.div>
        </Box>
        <Box as={Link} to="/" px={2} mt="6px">
          <Image src="/images/henophess.png" alt="Henophess Logo" height="40px" objectFit="contain" />
        </Box>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: clerk.loaded ? 1 : 0 }} transition={{ duration: 0.5 }}>
          <HStack>
            <HStack as={"nav"} spacing={4} display={{ base: "none", md: "flex" }} justify={"flex-end"} pr={4}>
              {TopMenuLinks.map((link) => (
                <NavLink key={link.label} item={link} />
              ))}
            </HStack>

            <IconButton
              aria-label="Toggle dark mode"
              icon={colorMode === "dark" ? <SunIcon /> : <MoonIcon />}
              onClick={toggleColorMode}
              variant="ghost"
              color={colorMode === "dark" ? "yellow.200" : "brand.primary"}
              _hover={{
                bg: colorMode === "dark" ? "gray.700" : "gray.200"
              }}
            />

            <SignedIn>
              <Tooltip label="Settings">
                <IconButton
                  aria-label="Settings"
                  icon={<FiSettings />}
                  onClick={() => navigate("/settings")}
                  variant="ghost"
                  color={colorMode === "dark" ? "gray.100" : "brand.primary"}
                  _hover={{
                    bg: colorMode === "dark" ? "gray.700" : "gray.200"
                  }}
                />
              </Tooltip>

              <Flex alignItems={"center"}>
                <Menu>
                  <MenuButton
                    as={Button}
                    rounded={"full"}
                    variant={"link"}
                    cursor={"pointer"}
                    minW={0}
                    aria-label="User Menu"
                    fontWeight={"400"}
                    _hover={{ textDecoration: "none" }}
                  >
                    <HStack>
                      <VStack spacing={0} alignItems="flex-end" hideBelow="md">
                        <Text color={colorMode === "dark" ? "gray.100" : "brand.primary"}>{user?.user?.firstName}</Text>
                        {userInfo?.isSuperAdmin && (
                          <Badge colorScheme="purple" fontSize="xs" variant="solid">
                            SuperAdmin
                          </Badge>
                        )}
                        {userInfo?.isAdmin && !userInfo?.isSuperAdmin && (
                          <Badge colorScheme="blue" fontSize="xs" variant="solid">
                            Admin
                          </Badge>
                        )}
                      </VStack>

                      <Box
                        color={colorMode === "dark" ? "gray.100" : "brand.primary"}
                        fontSize="2rem"
                        position="relative"
                      >
                        <CgProfile />
                        {(userInfo?.isSuperAdmin || userInfo?.isAdmin) && (
                          <Box
                            position="absolute"
                            top="-2px"
                            right="-2px"
                            w="10px"
                            h="10px"
                            borderRadius="full"
                            bg={userInfo?.isSuperAdmin ? "purple.500" : "blue.500"}
                            display={{ md: "none" }}
                          />
                        )}
                      </Box>
                    </HStack>
                  </MenuButton>
                  <MenuList
                    minW={"0"}
                    borderColor={colorMode === "dark" ? "gray.700" : "gray.300"}
                    bg={colorMode === "dark" ? "gray.800" : "white"}
                  >
                    {userInfo?.isSuperAdmin && (
                      <MenuItem
                        textAlign="center"
                        px={6}
                        bg={colorMode === "dark" ? "gray.800" : "white"}
                        _hover={{
                          bg: colorMode === "dark" ? "gray.700" : "gray.100"
                        }}
                        pointerEvents="none"
                      >
                        <Badge colorScheme="purple" w="full" textAlign="center">
                          SuperAdmin
                        </Badge>
                      </MenuItem>
                    )}
                    {userInfo?.isAdmin && !userInfo?.isSuperAdmin && (
                      <MenuItem
                        textAlign="center"
                        px={6}
                        bg={colorMode === "dark" ? "gray.800" : "white"}
                        _hover={{
                          bg: colorMode === "dark" ? "gray.700" : "gray.100"
                        }}
                        pointerEvents="none"
                      >
                        <Badge colorScheme="blue" w="full" textAlign="center">
                          Admin
                        </Badge>
                      </MenuItem>
                    )}
                    {(userInfo?.isSuperAdmin || userInfo?.isAdmin) && (
                      <MenuDivider borderColor={colorMode === "dark" ? "gray.700" : "gray.200"} />
                    )}
                    <MenuItem
                      onClick={() => navigate("/account")}
                      textAlign="center"
                      px={6}
                      bg={colorMode === "dark" ? "gray.800" : "white"}
                      _hover={{
                        bg: colorMode === "dark" ? "gray.700" : "gray.100"
                      }}
                    >
                      Account
                    </MenuItem>

                    <MenuItem
                      onClick={() => navigate("/settings")}
                      textAlign="center"
                      px={6}
                      bg={colorMode === "dark" ? "gray.800" : "white"}
                      _hover={{
                        bg: colorMode === "dark" ? "gray.700" : "gray.100"
                      }}
                    >
                      Settings
                    </MenuItem>

                    <MenuItem
                      onClick={() => navigate("/superuser")}
                      textAlign="center"
                      px={6}
                      bg={colorMode === "dark" ? "gray.800" : "white"}
                      _hover={{
                        bg: colorMode === "dark" ? "gray.700" : "gray.100"
                      }}
                      display={userInfo?.isSuperAdmin ? "block" : "none"}
                    >
                      Superuser
                    </MenuItem>
                    <MenuItem
                      onClick={() => navigate("/admin")}
                      textAlign="center"
                      px={6}
                      bg={colorMode === "dark" ? "gray.800" : "white"}
                      _hover={{
                        bg: colorMode === "dark" ? "gray.700" : "gray.100"
                      }}
                    >
                      Admin
                    </MenuItem>
                    <MenuDivider borderColor={colorMode === "dark" ? "gray.700" : "gray.200"} />
                    <MenuItem
                      onClick={handleSignOut}
                      px={6}
                      bg={colorMode === "dark" ? "gray.800" : "white"}
                      _hover={{
                        bg: colorMode === "dark" ? "gray.700" : "gray.100"
                      }}
                    >
                      Sign out
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Flex>
            </SignedIn>

            <SignedOut>
              <Button
                as={Link}
                to="/signin"
                variant="solid"
                bg={colorMode === "dark" ? "brand.dark_green" : "brand.primary"}
                color={colorMode === "dark" ? "white" : "black"}
                _hover={{
                  bg: colorMode === "dark" ? "brand.forest_green" : "brand.olive_green"
                }}
                size="md"
              >
                Login
              </Button>
            </SignedOut>
          </HStack>
        </motion.div>
      </Flex>
    </Box>
  );
}
